import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import HeaderMain from "./Components/HeaderMain";
import ImageModal from "../common/FullScreenImageModal.jsx";

export const PlayerProfile = () => {
  const { userId } = useParams();
  const [profileData, setProfileData] = useState(null);
  const [gameStats, setGameStats] = useState(null);
  const [instituteAffiliations, setInstituteAffiliations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [affiliationsLoading, setAffiliationsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalImage, setModalImage] = useState(null);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/api/common/get-public-user-profile/${userId}`
        );

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error("User not found");
          } else if (response.status === 400) {
            throw new Error("Invalid user ID");
          } else {
            throw new Error("Failed to fetch user profile");
          }
        }

        const data = await response.json();
        setProfileData(data);
      } catch (err) {
        console.error("Error fetching user profile:", err);
        setError(err.message || "Failed to load user profile");
      } finally {
        setLoading(false);
      }
    };

    const fetchGameStatistics = async () => {
      try {
        console.log("Fetching game statistics for user:", userId);

        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/user-add-game/api/selection/user-game-statistics/${userId}`,
          { withCredentials: true }
        );

        if (response.ok) {
          const data = await response.json();
          setGameStats(data.data);
        }
      } catch (err) {
        console.error("Error fetching game statistics:", err);
        // Don't show error for stats, just log it
      } finally {
        setStatsLoading(false);
      }
    };

    const fetchInstituteAffiliations = async () => {
      try {
        console.log("Fetching institute affiliations for user:", userId);

        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/api/common/get-user-institute-affiliations/${userId}`
        );

        if (response.ok) {
          const data = await response.json();
          setInstituteAffiliations(data.data);
        }
      } catch (err) {
        console.error("Error fetching institute affiliations:", err);
        // Don't show error for affiliations, just log it
      } finally {
        setAffiliationsLoading(false);
      }
    };

    if (userId && !isNaN(userId)) {
      fetchUserProfile();
      fetchGameStatistics();
      fetchInstituteAffiliations();
    } else {
      setError("Invalid user ID");
      setLoading(false);
      setStatsLoading(false);
      setAffiliationsLoading(false);
    }
  }, [userId]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{error}</span>
        </div>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-gray-600">No profile data available</div>
      </div>
    );
  }

  const getFullName = () => {
    if (!profileData) return "";
    const { first_name, middle_name, last_name } = profileData;
    const firstName = first_name || "";
    const middleName = middle_name ? middle_name + " " : "";
    const lastName = last_name || "";
    const fullName = `${firstName} ${middleName}${lastName}`.trim();
    return fullName || "Unknown User";
  };

  const getLocationString = () => {
    if (!profileData || !profileData.address) return "Location not specified";
    const { city, country } = profileData.address;
    if (!city && !country) return "Location not specified";
    return `${city || "Unknown City"}, ${country || "Unknown Country"}`;
  };

  const handleImageClick = (imageBase64) => {
    setModalImage(imageBase64);
    setIsModalOpen(true);
  };

  return (
    <>
      <HeaderMain pageType="player-profile" />
      <div className="min-h-screen bg-light-blue-500 py-4 sm:py-8">
        <div className="max-w-4xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          {/* Profile Header */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 h-20 sm:h-28 md:h-32"></div>

            <div className="relative px-3 sm:px-4 md:px-6 pb-4 sm:pb-6">
              {/* Profile Picture */}
              <div className="flex justify-center -mt-12 sm:-mt-14 md:-mt-16 mb-3 sm:mb-4">
                <div className="relative">
                  {profileData.picture ? (
                    <img
                      src={`data:image/jpeg;base64,${profileData.picture}`}
                      alt="Profile"
                      className="w-20 h-20 sm:w-28 sm:h-28 md:w-32 md:h-32 rounded-full border-3 sm:border-4 border-white shadow-lg object-cover cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => handleImageClick(profileData.picture)}
                    />
                  ) : (
                    <div className="w-20 h-20 sm:w-28 sm:h-28 md:w-32 md:h-32 rounded-full border-3 sm:border-4 border-white shadow-lg bg-gray-300 flex items-center justify-center">
                      <svg
                        className="w-10 h-10 sm:w-14 sm:h-14 md:w-16 md:h-16 text-gray-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  )}
                </div>
              </div>

              {/* User Info */}
              <div className="text-center">
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-1 sm:mb-2 px-2">
                  {getFullName()}
                </h1>
                {profileData.user_name && (
                  <p className="text-sm sm:text-base md:text-lg text-gray-600 mb-3 sm:mb-4">
                    @{profileData.user_name}
                  </p>
                )}
              </div>

              {/* Basic Information Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6 mt-4 sm:mt-6 md:mt-8">
                {/* Personal Info */}
                <div className="bg-gray-50 rounded-lg p-4 sm:p-5 md:p-6">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
                    <svg
                      className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-blue-600 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Personal Info
                  </h3>
                  <div className="space-y-2 sm:space-y-3">
                    <div>
                      <span className="text-xs sm:text-sm text-gray-500">Email</span>
                      <p className="text-sm sm:text-base text-gray-900 break-all">
                        {profileData.email || "Not specified"}
                      </p>
                    </div>
                    <div>
                      <span className="text-xs sm:text-sm text-gray-500">Age</span>
                      <p className="text-sm sm:text-base text-gray-900">
                        {profileData.age
                          ? `${profileData.age} years old`
                          : "Not specified"}
                      </p>
                    </div>
                    {profileData.gender && (
                      <div>
                        <span className="text-xs sm:text-sm text-gray-500">Gender</span>
                        <p className="text-sm sm:text-base text-gray-900 capitalize">
                          {profileData.gender}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Location */}
                <div className="bg-gray-50 rounded-lg p-4 sm:p-5 md:p-6">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
                    <svg
                      className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-green-600 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Location
                  </h3>
                  <p className="text-sm sm:text-base text-gray-900">{getLocationString()}</p>
                </div>

                {/* Sports Interests */}
                <div className="bg-gray-50 rounded-lg p-4 sm:p-5 md:p-6 sm:col-span-2 lg:col-span-1">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
                    <svg
                      className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-orange-600 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                    </svg>
                    Sports Interests
                  </h3>
                  <div className="space-y-2">
                    {profileData.sports_interests &&
                    profileData.sports_interests.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5 sm:gap-2">
                        {profileData.sports_interests.map((sport) => (
                          <span
                            key={sport.id}
                            className="inline-flex items-center px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm font-medium bg-blue-100 text-blue-800"
                          >
                            {sport.name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm sm:text-base text-gray-500 italic">
                        No sports interests specified
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Game Statistics Section */}
              {!statsLoading && gameStats && gameStats.overview && (
                <div className="mt-6 sm:mt-8">
                  <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center">
                    <svg
                      className="w-5 h-5 sm:w-6 sm:h-6 mr-2 text-purple-600 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Game Statistics
                  </h2>

                  {/* Overview Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 md:gap-4 mb-4 sm:mb-6">
                    <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-3 sm:p-4 text-white">
                      <div className="text-lg sm:text-xl md:text-2xl font-bold">
                        {gameStats.overview.totalMatches || 0}
                      </div>
                      <div className="text-blue-100 text-xs sm:text-sm">Total Matches</div>
                    </div>
                    <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-3 sm:p-4 text-white">
                      <div className="text-lg sm:text-xl md:text-2xl font-bold">
                        {gameStats.overview.totalWins || 0}
                      </div>
                      <div className="text-green-100 text-xs sm:text-sm">Wins</div>
                    </div>
                    <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-lg p-3 sm:p-4 text-white">
                      <div className="text-lg sm:text-xl md:text-2xl font-bold">
                        {gameStats.overview.totalLosses || 0}
                      </div>
                      <div className="text-red-100 text-xs sm:text-sm">Losses</div>
                    </div>
                    <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-3 sm:p-4 text-white">
                      <div className="text-lg sm:text-xl md:text-2xl font-bold">
                        {gameStats.overview.winRate || 0}%
                      </div>
                      <div className="text-purple-100 text-xs sm:text-sm">Win Rate</div>
                    </div>
                  </div>

                  {/* Sports Statistics */}
                  {gameStats.sportStatistics &&
                    Object.keys(gameStats.sportStatistics).length > 0 && (
                      <div className="bg-white rounded-lg shadow-lg p-4 sm:p-5 md:p-6 mb-4 sm:mb-6">
                        <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">
                          Sport-by-Sport Performance
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                          {Object.entries(gameStats.sportStatistics).map(
                            ([sport, stats]) => (
                              <div
                                key={sport}
                                className="border rounded-lg p-3 sm:p-4 hover:shadow-md transition-shadow"
                              >
                                <div className="flex items-center justify-between mb-2 sm:mb-3">
                                  <h4 className="font-semibold text-gray-800 text-sm sm:text-base">
                                    {sport}
                                  </h4>
                                  <span className="text-xs sm:text-sm text-gray-500">
                                    {stats.winRate}% win rate
                                  </span>
                                </div>

                                <div className="grid grid-cols-3 gap-1 sm:gap-2 text-xs sm:text-sm">
                                  <div className="text-center">
                                    <div className="font-bold text-blue-600">
                                      {stats.totalMatches}
                                    </div>
                                    <div className="text-gray-500 text-xs">Matches</div>
                                  </div>
                                  <div className="text-center">
                                    <div className="font-bold text-green-600">
                                      {stats.wins}
                                    </div>
                                    <div className="text-gray-500 text-xs">Wins</div>
                                  </div>
                                  <div className="text-center">
                                    <div className="font-bold text-red-600">
                                      {stats.losses}
                                    </div>
                                    <div className="text-gray-500 text-xs">Losses</div>
                                  </div>
                                </div>

                                {/* Sport-specific stats */}
                                {(stats.goals > 0 ||
                                  stats.assists > 0 ||
                                  stats.points > 0 ||
                                  stats.strokes > 0 ||
                                  stats.potSuccessRate > 0 ||
                                  stats.runsScored > 0 ||
                                  stats.wicketsTaken > 0 ||
                                  stats.penalties > 0 ||
                                  stats.averagePlace > 0 ||
                                  stats.averagePointsPerGame > 0) && (
                                  <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-gray-200">
                                    <div className="grid grid-cols-2 gap-1 sm:gap-2 text-xs sm:text-sm">
                                      {/* General sports stats */}
                                      {stats.goals > 0 && (
                                        <div className="text-center">
                                          <div className="font-bold text-orange-600">
                                            {stats.goals}
                                          </div>
                                          <div className="text-gray-500">
                                            Goals
                                          </div>
                                        </div>
                                      )}
                                      {stats.assists > 0 && (
                                        <div className="text-center">
                                          <div className="font-bold text-yellow-600">
                                            {stats.assists}
                                          </div>
                                          <div className="text-gray-500">
                                            Assists
                                          </div>
                                        </div>
                                      )}
                                      {stats.points > 0 &&
                                        sport !== "Swimming" &&
                                        sport !== "Squash" &&
                                        sport !== "Hockey" && (
                                          <div className="text-center">
                                            <div className="font-bold text-indigo-600">
                                              {stats.points}
                                            </div>
                                            <div className="text-gray-500">
                                              Points
                                            </div>
                                          </div>
                                        )}
                                      {stats.setsWon > 0 && (
                                        <div className="text-center">
                                          <div className="font-bold text-teal-600">
                                            {stats.setsWon}
                                          </div>
                                          <div className="text-gray-500">
                                            Sets Won
                                          </div>
                                        </div>
                                      )}

                                      {/* Cricket-specific stats */}
                                      {stats.runsScored > 0 && (
                                        <div className="text-center">
                                          <div className="font-bold text-orange-600">
                                            {stats.runsScored}
                                          </div>
                                          <div className="text-gray-500">
                                            Runs
                                          </div>
                                        </div>
                                      )}
                                      {stats.wicketsTaken > 0 && (
                                        <div className="text-center">
                                          <div className="font-bold text-red-600">
                                            {stats.wicketsTaken}
                                          </div>
                                          <div className="text-gray-500">
                                            Wickets
                                          </div>
                                        </div>
                                      )}
                                      {stats.fours > 0 && (
                                        <div className="text-center">
                                          <div className="font-bold text-blue-600">
                                            {stats.fours}
                                          </div>
                                          <div className="text-gray-500">
                                            Fours
                                          </div>
                                        </div>
                                      )}
                                      {stats.sixes > 0 && (
                                        <div className="text-center">
                                          <div className="font-bold text-purple-600">
                                            {stats.sixes}
                                          </div>
                                          <div className="text-gray-500">
                                            Sixes
                                          </div>
                                        </div>
                                      )}
                                      {stats.averageStrikeRate > 0 && (
                                        <div className="text-center">
                                          <div className="font-bold text-green-600">
                                            {stats.averageStrikeRate}%
                                          </div>
                                          <div className="text-gray-500">
                                            Strike Rate
                                          </div>
                                        </div>
                                      )}
                                      {stats.averageEconomyRate > 0 && (
                                        <div className="text-center">
                                          <div className="font-bold text-indigo-600">
                                            {stats.averageEconomyRate}
                                          </div>
                                          <div className="text-gray-500">
                                            Economy Rate
                                          </div>
                                        </div>
                                      )}
                                      {stats.oversBowled > 0 && (
                                        <div className="text-center">
                                          <div className="font-bold text-pink-600">
                                            {stats.oversBowled}
                                          </div>
                                          <div className="text-gray-500">
                                            Overs
                                          </div>
                                        </div>
                                      )}

                                      {/* Hockey-specific stats */}
                                      {stats.penalties > 0 && (
                                        <div className="text-center">
                                          <div className="font-bold text-red-600">
                                            {stats.penalties}
                                          </div>
                                          <div className="text-gray-500">
                                            Penalties
                                          </div>
                                        </div>
                                      )}

                                      {/* Swimming-specific stats */}
                                      {stats.averagePlace > 0 &&
                                        typeof stats.averagePlace ===
                                          "number" && (
                                          <div className="text-center">
                                            <div className="font-bold text-cyan-600">
                                              {stats.averagePlace.toFixed(1)}
                                            </div>
                                            <div className="text-gray-500">
                                              Avg Place
                                            </div>
                                          </div>
                                        )}
                                      {sport === "Swimming" &&
                                        stats.silverMedals > 0 && (
                                          <div className="text-center">
                                            <div className="font-bold text-gray-600">
                                              {stats.silverMedals}
                                            </div>
                                            <div className="text-gray-500">
                                              🥈 Silver
                                            </div>
                                          </div>
                                        )}
                                      {sport === "Swimming" &&
                                        stats.bronzeMedals > 0 && (
                                          <div className="text-center">
                                            <div className="font-bold text-orange-600">
                                              {stats.bronzeMedals}
                                            </div>
                                            <div className="text-gray-500">
                                              🥉 Bronze
                                            </div>
                                          </div>
                                        )}

                                      {/* Squash-specific stats */}
                                      {stats.averagePointsPerGame > 0 &&
                                        typeof stats.averagePointsPerGame ===
                                          "number" && (
                                          <div className="text-center">
                                            <div className="font-bold text-violet-600">
                                              {stats.averagePointsPerGame.toFixed(
                                                1
                                              )}
                                            </div>
                                            <div className="text-gray-500">
                                              Avg Pts/Game
                                            </div>
                                          </div>
                                        )}

                                      {/* Golf-specific stats */}
                                      {stats.strokes > 0 && (
                                        <div className="text-center">
                                          <div className="font-bold text-green-600">
                                            {stats.strokes}
                                          </div>
                                          <div className="text-gray-500">
                                            Total Strokes
                                          </div>
                                        </div>
                                      )}
                                      {stats.averageStrokesPerHole > 0 &&
                                        typeof stats.averageStrokesPerHole ===
                                          "number" && (
                                          <div className="text-center">
                                            <div className="font-bold text-blue-600">
                                              {stats.averageStrokesPerHole.toFixed(
                                                1
                                              )}
                                            </div>
                                            <div className="text-gray-500">
                                              Avg/Hole
                                            </div>
                                          </div>
                                        )}

                                      {/* Snooker-specific stats */}
                                      {stats.potSuccessRate > 0 &&
                                        typeof stats.potSuccessRate ===
                                          "number" && (
                                          <div className="text-center">
                                            <div className="font-bold text-emerald-600">
                                              {stats.potSuccessRate.toFixed(1)}%
                                            </div>
                                            <div className="text-gray-500">
                                              Pot Success
                                            </div>
                                          </div>
                                        )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )}

                  {/* Achievements */}
                  {gameStats.achievements &&
                    Array.isArray(gameStats.achievements) &&
                    gameStats.achievements.length > 0 && (
                      <div className="bg-white rounded-lg shadow-lg p-4 sm:p-5 md:p-6 mb-4 sm:mb-6">
                        <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
                          <svg
                            className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-yellow-500 flex-shrink-0"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Achievements
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
                          {gameStats.achievements.map((achievement, index) => (
                            <div
                              key={index}
                              className="bg-gradient-to-r from-yellow-50 to-yellow-100 border border-yellow-200 rounded-lg p-2 sm:p-3"
                            >
                              <div className="font-semibold text-yellow-800 text-sm sm:text-base">
                                {achievement.title}
                              </div>
                              <div className="text-xs sm:text-sm text-yellow-600">
                                {achievement.description}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                  {/* Recent Activity */}
                  {gameStats.recentActivity &&
                    Array.isArray(gameStats.recentActivity) &&
                    gameStats.recentActivity.length > 0 && (
                      <div className="bg-white rounded-lg shadow-lg p-4 sm:p-5 md:p-6">
                        <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
                          <svg
                            className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-blue-500 flex-shrink-0"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Recent Activity
                        </h3>
                        <div className="space-y-2 sm:space-y-3">
                          {gameStats.recentActivity
                            .slice(0, 5)
                            .map((activity, index) => (
                              <div
                                key={index}
                                className="flex items-center justify-between py-1.5 sm:py-2 border-b border-gray-100 last:border-b-0"
                              >
                                <div className="flex items-center">
                                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-500 rounded-full mr-2 sm:mr-3 flex-shrink-0"></div>
                                  <span className="font-medium text-gray-800 text-sm sm:text-base">
                                    {activity.sport}
                                  </span>
                                </div>
                                <span className="text-xs sm:text-sm text-gray-500">
                                  {new Date(activity.date).toLocaleDateString()}
                                </span>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                </div>
              )}

              {/* No statistics message */}
              {!statsLoading &&
                (!gameStats ||
                  !gameStats.overview ||
                  gameStats.overview.totalMatches === 0) && (
                  <div className="mt-6 sm:mt-8 bg-gray-50 rounded-lg p-4 sm:p-6 text-center">
                    <svg
                      className="w-10 h-10 sm:w-12 sm:h-12 mx-auto text-gray-400 mb-3 sm:mb-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                      />
                    </svg>
                    <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-1 sm:mb-2">
                      No Game Statistics
                    </h3>
                    <p className="text-sm sm:text-base text-gray-600">
                      This user hasn't played any recorded games yet.
                    </p>
                  </div>
                )}

              {/* Institute Affiliations Section */}
              {!affiliationsLoading && instituteAffiliations && (
                <div className="mt-6 sm:mt-8">
                  <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center">
                    <svg
                      className="w-5 h-5 sm:w-6 sm:h-6 mr-2 text-indigo-600 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.84L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.84l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
                    </svg>
                    Institute Affiliations
                  </h2>

                  {instituteAffiliations.totalAffiliations === 0 ? (
                    <div className="bg-gray-50 rounded-lg p-4 sm:p-6 md:p-8 text-center">
                      <svg
                        className="w-10 h-10 sm:w-12 sm:h-12 text-gray-300 mx-auto mb-3 sm:mb-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                        />
                      </svg>
                      <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-1 sm:mb-2">
                        No Institute Affiliations
                      </h3>
                      <p className="text-sm sm:text-base text-gray-600">
                        This user is not affiliated with any institutes.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4 sm:space-y-6">
                      {/* Active Affiliations */}
                      {instituteAffiliations.activeAffiliations.length > 0 && (
                        <div>
                          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
                            <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-green-500 rounded-full mr-2 flex-shrink-0"></span>
                            Active Memberships (
                            {instituteAffiliations.activeAffiliations.length})
                          </h3>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                            {instituteAffiliations.activeAffiliations.map(
                              (affiliation) => (
                                <div
                                  key={affiliation.id}
                                  className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 shadow-sm hover:shadow-md transition-shadow"
                                >
                                  <div className="flex items-start space-x-2 sm:space-x-3">
                                    {affiliation.institute_logo ? (
                                      <img
                                        src={`data:image/jpeg;base64,${affiliation.institute_logo}`}
                                        alt={`${affiliation.institute_name} logo`}
                                        className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover border-2 border-gray-200 cursor-pointer hover:opacity-90 transition-opacity flex-shrink-0"
                                        onClick={() =>
                                          handleImageClick(
                                            affiliation.institute_logo
                                          )
                                        }
                                        onError={(e) => {
                                          e.target.style.display = "none";
                                        }}
                                      />
                                    ) : (
                                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                                        <svg
                                          className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600"
                                          fill="currentColor"
                                          viewBox="0 0 20 20"
                                        >
                                          <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.84L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.84l-7-3z" />
                                        </svg>
                                      </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                      <h4 className="font-semibold text-gray-900 text-sm sm:text-base truncate">
                                        {affiliation.institute_name}
                                      </h4>
                                      {affiliation.institute_type_name && (
                                        <p className="text-xs sm:text-sm text-gray-500 capitalize">
                                          {affiliation.institute_type_name}
                                        </p>
                                      )}
                                      {(affiliation.city ||
                                        affiliation.country) && (
                                        <p className="text-xs sm:text-sm text-gray-500">
                                          {affiliation.city &&
                                          affiliation.country
                                            ? `${affiliation.city}, ${affiliation.country}`
                                            : affiliation.city ||
                                              affiliation.country}
                                        </p>
                                      )}
                                      <div className="mt-1.5 sm:mt-2 flex flex-wrap items-center gap-2 sm:gap-4 text-xs text-gray-500">
                                        <span>
                                          Joined:{" "}
                                          {new Date(
                                            affiliation.joined_at
                                          ).toLocaleDateString()}
                                        </span>
                                        <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                          Active
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      )}

                      {/* Former Affiliations */}
                      {instituteAffiliations.formerAffiliations.length > 0 && (
                        <div>
                          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
                            <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-gray-400 rounded-full mr-2 flex-shrink-0"></span>
                            Former Memberships (
                            {instituteAffiliations.formerAffiliations.length})
                          </h3>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                            {instituteAffiliations.formerAffiliations.map(
                              (affiliation) => (
                                <div
                                  key={affiliation.id}
                                  className="bg-gray-50 border border-gray-200 rounded-lg p-3 sm:p-4 shadow-sm"
                                >
                                  <div className="flex items-start space-x-2 sm:space-x-3">
                                    {affiliation.institute_logo ? (
                                      <img
                                        src={`data:image/jpeg;base64,${affiliation.institute_logo}`}
                                        alt={`${affiliation.institute_name} logo`}
                                        className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover border-2 border-gray-200 opacity-75 cursor-pointer hover:opacity-60 transition-opacity flex-shrink-0"
                                        onClick={() =>
                                          handleImageClick(
                                            affiliation.institute_logo
                                          )
                                        }
                                        onError={(e) => {
                                          e.target.style.display = "none";
                                        }}
                                      />
                                    ) : (
                                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                                        <svg
                                          className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500"
                                          fill="currentColor"
                                          viewBox="0 0 20 20"
                                        >
                                          <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.84L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.84l-7-3z" />
                                        </svg>
                                      </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                      <h4 className="font-semibold text-gray-700 text-sm sm:text-base truncate">
                                        {affiliation.institute_name}
                                      </h4>
                                      {affiliation.institute_type_name && (
                                        <p className="text-xs sm:text-sm text-gray-500 capitalize">
                                          {affiliation.institute_type_name}
                                        </p>
                                      )}
                                      {(affiliation.city ||
                                        affiliation.country) && (
                                        <p className="text-xs sm:text-sm text-gray-500">
                                          {affiliation.city &&
                                          affiliation.country
                                            ? `${affiliation.city}, ${affiliation.country}`
                                            : affiliation.city ||
                                              affiliation.country}
                                        </p>
                                      )}
                                      <div className="mt-1.5 sm:mt-2 flex flex-wrap items-center gap-2 sm:gap-4 text-xs text-gray-500">
                                        <span>
                                          Joined:{" "}
                                          {new Date(
                                            affiliation.joined_at
                                          ).toLocaleDateString()}
                                        </span>
                                        {affiliation.left_at && (
                                          <span>
                                            Left:{" "}
                                            {new Date(
                                              affiliation.left_at
                                            ).toLocaleDateString()}
                                          </span>
                                        )}
                                        <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                          Former
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ImageModal
        images={modalImage}
        initialIndex={0}
        onClose={() => setIsModalOpen(false)}
        isOpen={isModalOpen}
        alt="Profile Image"
        isBase64={true}
      />
    </>
  );
};
